import React, { useEffect, useState } from 'react';

import Notification from '@rio-cloud/rio-uikit/Notification';

import { saveNavigationTip, updateNavigationTip } from '../../../../services/navigationTips';
import { EInfractionType, INavigationTip } from '../../../../types/navigationTips';
import EditForm from './EditForm';
import EditHeader from './EditHeader';

interface EditProps {
  onBack: () => void;
  mode: 'create' | 'edit';
  tip?: INavigationTip;
}

const initialForm = {
  infractionType: EInfractionType.IT_IDLING as EInfractionType | '',
  description: '',
  text: '',
  videoLink: '',
};

const Edit: React.FC<EditProps> = ({ onBack, mode, tip }) => {
  const [form, setForm] = useState(initialForm);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFormChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelected = (file: File | null) => {
    setSelectedFile(file);
  };

  useEffect(() => {
    if (mode === 'edit' && tip) {
      setForm({
        infractionType: tip.infractionType || '',
        description: tip.description || '',
        text: tip.text || '',
        videoLink: tip.videoLink || '',
      });
      setSelectedFile(null);
    } else if (mode === 'create') {
      setForm(initialForm);
      setSelectedFile(null);
    }
  }, [mode, tip]);

  const handleSubmit = async () => {
    if (!form.infractionType) {
      Notification.error('Por favor, selecione o tipo de infração.');
      return;
    }

    if (!form.description.trim()) {
      Notification.error('Por favor, preencha a descrição do tipo de infração.');
      return;
    }

    if (!form.text.trim()) {
      Notification.error('Por favor, preencha o texto da dica.');
      return;
    }

    // Validação: deve ter arquivo OU link de vídeo
    if (!selectedFile && !form.videoLink) {
      Notification.error('Por favor, selecione um arquivo de vídeo ou preencha um link de vídeo.');
      return;
    }

    // Se há link de vídeo, validar se é um link válido
    if (form.videoLink && !form.videoLink.startsWith('http')) {
      Notification.error('Por favor, insira um link válido para o vídeo (deve começar com http:// ou https://).');
      return;
    }

    const payloadBase: Omit<INavigationTip, 'id'> = {
      infractionType: form.infractionType as EInfractionType,
      description: form.description,
      text: form.text,
      videoLink: form.videoLink,
    };

    // Log para debug
    console.log('Dados do formulário antes de enviar:', {
      mode,
      form,
      selectedFile: selectedFile ? {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type
      } : null,
      payloadBase
    });

    try {
      if (mode === 'create') {
        console.log('Chamando saveNavigationTip...');
        await saveNavigationTip(payloadBase, selectedFile || undefined);
        Notification.success('Dica de navegação criada com sucesso!');
      } else if (mode === 'edit' && tip) {
        console.log('Chamando updateNavigationTip...');
        const payloadWithId: INavigationTip = {
          id: tip.id,
          ...payloadBase,
        };
        await updateNavigationTip(tip.id.toString(), payloadWithId, selectedFile || undefined);
        Notification.success('Dica de navegação atualizada com sucesso!');
      }

      onBack();
    } catch (err: any) {
      console.error('Erro detalhado:', err);
      console.error('Response data:', err?.response?.data);
      console.error('Response status:', err?.response?.status);
      Notification.error(err?.response?.data?.error || err?.message || 'Erro ao salvar dica de navegação.');
    }
  };

  return (
    <div className="min-h-screen text-color-info">
      <div className="width-70pct margin-x-auto padding-50">
        <div className="margin-top-40 margin-bottom-32 padding-y-10">
          <EditHeader mode={mode} />
        </div>
        <div className="panel panel-default panel-body shadow-default rounded text-color-info">
          <EditForm
            form={form}
            onFormChange={handleFormChange}
            onFileSelected={handleFileSelected}
            onBack={onBack}
            onSubmit={handleSubmit}
            mode={mode}
          />
        </div>
      </div>
    </div>
  );
};

export default Edit; 